// Pure date logic for the court statistics screen.
//
// Everything works on "YYYY-MM-DD" strings (the same local-calendar-day format the stats endpoint
// takes and returns) and does its arithmetic in UTC, so a daylight-saving jump or the browser's time
// zone can never shift a day. The only place the browser's local clock is read is `toYmd`, which
// turns the caller-supplied "today" into the local calendar day.

const DAY_MS = 86400000;
const pad = (n) => String(n).padStart(2, "0");

// A range is `{ from, to }` (YYYY-MM-DD), both ends included.
const parts = (ymd) => ymd.split("-").map(Number);
const toUtcMs = (ymd) => {
  const [y, m, d] = parts(ymd);
  return Date.UTC(y, m - 1, d);
};
const fromUtcMs = (ms) => {
  const d = new Date(ms);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
};

/** Local calendar day of a `Date` as YYYY-MM-DD. */
export const toYmd = (date) => `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

export const addDays = (ymd, n) => fromUtcMs(toUtcMs(ymd) + n * DAY_MS);

export const daysInRange = ({ from, to }) => Math.round((toUtcMs(to) - toUtcMs(from)) / DAY_MS) + 1;

/** How many times each weekday (0 = Sunday) falls inside the range; used to average heat cells. */
export const weekdayCounts = ({ from, to }) => {
  const counts = [0, 0, 0, 0, 0, 0, 0];
  const start = toUtcMs(from);
  for (let i = 0, n = daysInRange({ from, to }); i < n; i += 1) {
    counts[new Date(start + i * DAY_MS).getUTCDay()] += 1;
  }
  return counts;
};

const firstOfMonth = (ymd, monthOffset = 0) => {
  const [y, m] = parts(ymd);
  return fromUtcMs(Date.UTC(y, m - 1 + monthOffset, 1));
};
const lastOfMonth = (ymd) => addDays(firstOfMonth(ymd, 1), -1);

/**
 * Resolves a period preset to a concrete range. Every preset ends yesterday (local), the last day
 * with complete data; today is still being booked. "Este mes" therefore reads yesterday's month, so
 * on the 1st it shows the month that just closed instead of an empty range.
 *
 * @param {"last7"|"last30"|"last90"|"month"|"prevMonth"} preset
 * @param {Date} today  Local clock reading, injected so the function stays deterministic.
 */
export const resolvePeriod = (preset, today) => {
  const yesterday = addDays(toYmd(today), -1);
  switch (preset) {
    case "last7":
      return { from: addDays(yesterday, -6), to: yesterday };
    case "last90":
      return { from: addDays(yesterday, -89), to: yesterday };
    case "month":
      return { from: firstOfMonth(yesterday), to: yesterday };
    case "prevMonth": {
      const from = firstOfMonth(yesterday, -1);
      return { from, to: lastOfMonth(from) };
    }
    default:
      return { from: addDays(yesterday, -29), to: yesterday };
  }
};

/**
 * Comparison range for a resolved period.
 * - `none`: no comparison (null).
 * - `yoy`: minus 364 days (52 weeks) so weekdays line up with the current range.
 * - `prev`: calendar presets compare like with like ("Este mes" against the same stretch of the
 *   previous month, clamped to that month's length; "Mes anterior" against the whole month before
 *   it); rolling presets compare with the same number of days immediately before.
 *
 * @param {{from: string, to: string}} range
 * @param {string} preset
 * @param {"prev"|"yoy"|"none"} mode
 */
export const resolveComparison = (range, preset, mode) => {
  if (mode === "none" || !range) return null;
  if (mode === "yoy") return { from: addDays(range.from, -364), to: addDays(range.to, -364) };

  if (preset === "month" || preset === "prevMonth") {
    const from = firstOfMonth(range.from, -1);
    const monthEnd = lastOfMonth(from);
    if (preset === "prevMonth") return { from, to: monthEnd };
    const stretchEnd = addDays(from, daysInRange(range) - 1);
    return { from, to: stretchEnd > monthEnd ? monthEnd : stretchEnd };
  }

  const length = daysInRange(range);
  return { from: addDays(range.from, -length), to: addDays(range.to, -length) };
};

/** Daily points up to 45 days, weekly (daily average) above, so long ranges stay readable. */
export const bucketSize = (days) => (days > 45 ? 7 : 1);
